import Card from '@/components/elements/Card/Card';
import { Button, LinkButton } from '@/components/elements/forms/buttons';
import InputField from '@/components/elements/inputfields/InputField';
import MerchantLogo from '@/components/elements/MerchantLogo/MerchantLogo';
import Alert from '@/components/elements/notifications/Alert/Alert';
import { LoadingIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import CheckItem from './components/CheckItem';
import GiftCardDisplay from './components/GiftCardDisplay';
import ConfirmReturn from './components/modals/ConfirmReturn';
import ConfirmSendReceipt from './components/modals/ConfirmSendReciept';
import SuccessConfirmation from './components/modals/SuccessConfirmation';
import useGiftcardSelfServe from './GiftcardSelfServe.hooks';
import { getGiftCardButtonText, giftCardTitle } from './helpers/translations';
import { getUseGiftCardUrl } from './helpers/url';
import { isInvalidCode, isValidCode } from './helpers/validations';

const baseTranslation = 'features.giftcard-self-serve.GiftcardSelfServe';

const GiftcardSelfServe = () => {
  const { isMobileView } = useMobileView();
  const {
    dispatch,
    giftCard,
    codeInput,
    ssnInput,
    returnError,
    confirmLoading,
    loading,
    modalView,
    handleChangeCode,
    handleChangeSsn,
    handleSendReceipt,
    handleRefund,
    handleSubmitCode,
  } = useGiftcardSelfServe();

  const isReturned = giftCard?.returnedAt;
  const hasExpired = new Date(giftCard?.expiryDateTimeTz) < new Date();

  return (
    <>
      <div>
        <h1 className="text-h-l mb-sm font-bold">{_t(`${baseTranslation}.header`)}</h1>
        <h2 className="text-fg_secondary text-l">{_t(`${baseTranslation}.subHeader`)}</h2>
      </div>
      <Card childClassName={isMobileView ? '!p-xl' : '!p-2xl'}>
        <div className={`gap-2xl flex flex-col ${isMobileView ? '' : 'w-[568px]'}`}>
          <form className={`gap-md flex ${isMobileView ? 'flex-col' : ''}`}>
            <div className="flex-1">
              <InputField
                id="giftCardCode"
                label={_t(`${baseTranslation}.input.label`)}
                placeholder={_t(`${baseTranslation}.input.placeholder`)}
                value={codeInput.value}
                rightPadding={false}
                leftPadding={false}
                handleClear={() => dispatch({ type: 'SET_CODE', payload: '' })}
                rightIcon={
                  isValidCode(codeInput.value) && !codeInput.error && <Icon variant="check" color="fg_positive" />
                }
                error={
                  codeInput.error
                    ? ' '
                    : isInvalidCode(codeInput.value)
                    ? _t(`${baseTranslation}.error.invalidFormat`)
                    : ''
                }
                onChange={(e) => handleChangeCode(e.target.value)}
              />
            </div>
            <div>
              <Button block={isMobileView} size="md" onClick={handleSubmitCode}>
                {_t(`${baseTranslation}.fetchButton`)}
              </Button>
            </div>
          </form>

          {returnError && (
            <Alert
              body={returnError}
              variant="cation"
              verticalAlign="top"
              leftSlot={<Icon variant="warning" color="fg_notice_bold" />}
            />
          )}

          {codeInput.error && (
            <Alert
              body={codeInput.error}
              variant="warning"
              verticalAlign="top"
              leftSlot={<Icon variant="warning" color="fg_notice_bold" />}
            />
          )}

          {!giftCard && !loading && !codeInput.error && (
            <div className="gap-md flex flex-col">
              {_t(`${baseTranslation}.checkItems`).map((text, i) => (
                <CheckItem key={i} text={text} />
              ))}
            </div>
          )}

          {loading ? (
            <LoadingIcon className="mx-auto mt-10 block h-8 w-8" />
          ) : (
            giftCard && (
              <GiftCardDisplay
                {...{
                  ...giftCard,
                  title: giftCardTitle(giftCard.type, giftCard.expiryDateTimeTz, Boolean(giftCard.returnedAt)),
                }}
              />
            )
          )}

          {!loading && giftCard && (
            <div className={`flex flex-col ${isMobileView ? 'gap-md' : 'gap-xl'}`}>
              {(giftCard.type === 'place' || giftCard.type === 'value') && !hasExpired && (
                <div className="border-border_regular pb-md flex justify-between border-b">
                  <div className="gap-2xs flex flex-col">
                    <span className={classnames('text-fg_secondary', isMobileView && 'text-m')}>
                      {_t(`${baseTranslation}.validAt`)}
                    </span>
                    <span className={!isMobileView && 'text-xl'}>{giftCard?.practitioners?.join(', ')}</span>
                    {giftCard.address && (
                      <span className={classnames('text-fg_secondary', isMobileView && 'text-m')}>
                        {giftCard?.address?.street}, {giftCard?.address?.zipcode} {giftCard?.address?.city}
                      </span>
                    )}
                  </div>
                  {giftCard?.image && <MerchantLogo imgUrl={giftCard.image} alt="" size={isMobileView ? 'md' : 'lg'} />}
                </div>
              )}
              {!hasExpired && !isReturned && (
                <p className={`text-fg_secondary ${isMobileView ? 'text-m' : 'text-l'}`}>
                  <>{_t(`${baseTranslation}.description.${giftCard.type}`)}</>
                </p>
              )}
              <div className={`flex justify-between ${isMobileView ? 'gap-md flex-col' : ''}`}>
                {isMobileView && !hasExpired && !isReturned && (
                  <LinkButton
                    variant="primary"
                    size="md"
                    to={getUseGiftCardUrl(giftCard.type, giftCard.slug, giftCard.subscriberResourceId)}>
                    {getGiftCardButtonText(giftCard.type)}
                  </LinkButton>
                )}
                <div>
                  <Button
                    className="!pl-0"
                    variant="link"
                    size="md"
                    onClick={() => dispatch({ type: 'SET_MODAL_VIEW', payload: 'receipt' })}>
                    {_t(`${baseTranslation}.sendReceiptCta`)}
                  </Button>
                  {!hasExpired && !isReturned && (
                    <Button
                      variant="link"
                      size="md"
                      onClick={() => dispatch({ type: 'SET_MODAL_VIEW', payload: 'refund' })}>
                      {_t(`${baseTranslation}.returnGiftcardCta`)}
                    </Button>
                  )}
                </div>

                {!isMobileView && !hasExpired && !isReturned && (
                  <LinkButton
                    variant="primary"
                    size="md"
                    to={getUseGiftCardUrl(giftCard.type, giftCard.slug, giftCard.subscriberResourceId)}>
                    {getGiftCardButtonText(giftCard.type)}
                  </LinkButton>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>
      <ConfirmReturn
        isOpen={modalView === 'refund'}
        giftcardType={giftCard?.type}
        handleRefund={handleRefund}
        onCancel={() => dispatch({ type: 'SET_MODAL_VIEW', payload: null })}
        loading={confirmLoading}
      />
      <ConfirmSendReceipt
        isOpen={modalView === 'receipt'}
        loading={confirmLoading}
        buyerEmail={giftCard?.buyerEmail}
        giftcardType={giftCard?.type}
        ssnInput={ssnInput}
        handleClear={() => dispatch({ type: 'SET_SSN', payload: '' })}
        handleChangeSsn={handleChangeSsn}
        handleSendReceipt={handleSendReceipt}
        onCancel={() => dispatch({ type: 'SET_MODAL_VIEW', payload: null })}
      />
      <SuccessConfirmation
        isOpen={['receiptConfirmation', 'refundConfirmation'].includes(modalView)}
        onClose={() => dispatch({ type: 'RESET' })}
        buyerEmail={giftCard?.buyerEmail}
        modalView={modalView}
      />
    </>
  );
};

export default GiftcardSelfServe;
